"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { X, ImagePlus } from "lucide-react";
import { useGroupStore } from "@/store/groupStore";
import { useLanguage } from "@/context/LanguageContext";
import { getGroupCategoryLabelI18n, sortByLabel } from "@/lib/taxonomy-labels";
import { FieldError, SegmentedToggle } from "@/components/ui";
import { FORM_LIMITS } from "@/lib/formLimits";
import {
  MAX_MULTI_IMAGE_UPLOAD_COUNT,
  getImageUploadCopy,
  prepareAvatarUpload,
  prepareImageUploads,
} from "@/lib/image-upload";
import { useContentModeration } from "@/hooks/useContentModeration";
import { CrisisModal } from "./CrisisModal";
import { InappropriateLanguageModal } from "./InappropriateLanguageModal";
import { InappropriateLanguageBanner } from "./InappropriateLanguageBanner";

type FormErrors = {
  name?: string;
  categories?: string;
  description?: string;
};

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  // MWBI-890 — `isPrivate` is deliberately absent. The old Public/Private
  // switch derived it, coupling directory visibility to list visibility. The
  // two are now decoupled: this modal never sets `isPrivate`, so the schema
  // default (false) applies and new groups stay listed in the directory.
  onCreate: (data: { name: string; description: string; categorySlugs: string[]; websiteLink: string; acceptingMembers: boolean; memberListVisible: boolean; eventListVisible: boolean; memberApprovalMode: 'auto' | 'manual'; image: File | null; images?: File[]; avatar?: File | null; rulesText: string }) => Promise<{ success: boolean; groupDocumentId?: string }>;
}

const MAX_IMAGES = MAX_MULTI_IMAGE_UPLOAD_COUNT;
// MWBI-798 — must match MAX_GROUP_CATEGORIES in cms/server/src/api/group/controllers/group.ts
// and MAX_CATEGORIES in app/groups/[documentId]/page.tsx.
const MAX_CATEGORIES = 3;

export default function CreateGroupModal({ isOpen, onClose, onCreate }: CreateGroupModalProps) {
  const { t, language } = useLanguage();
  const photosCopy = getImageUploadCopy(language, 'multiple');
  const avatarCopy = getImageUploadCopy(language, 'avatar');
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [categorySlugs, setCategorySlugs] = useState<string[]>([]);
  const availableCategories = useGroupStore((s) => s.categories);
  const fetchGroupCategories = useGroupStore((s) => s.fetchCategories);

  // Fetch the canonical group-category list when the modal opens so the
  // dropdown options match what's actually in the CMS.
  useEffect(() => {
    if (isOpen) {
      void fetchGroupCategories();
    }
  }, [isOpen, fetchGroupCategories]);
  const [acceptingMembers, setAcceptingMembers] = useState(true);
  const [memberApprovalMode, setMemberApprovalMode] = useState<'auto' | 'manual'>('manual');
  // MWBI-890 — two independent visibility switches. Stored as the positive
  // "visible" sense to match the schema fields they map to 1:1; the switches
  // present the inverted "hidden" sense for the UI.
  const [memberListVisible, setMemberListVisible] = useState(true);
  const [eventListVisible, setEventListVisible] = useState(true);
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageNotice, setImageNotice] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarNotice, setAvatarNotice] = useState<string | null>(null);
  const [rulesText, setRulesText] = useState(t.modals.group.defaultRulesText);
  // MWBI-841 — keep the default rules text in sync with the active language.
  // `useState(initial)` only reads the initial value once, so without this the
  // textarea would otherwise stay frozen at whichever language was active at
  // first mount (the modal is always mounted by CreateButton).
  useEffect(() => {
    if (isOpen) {
      setRulesText(t.modals.group.defaultRulesText);
    }
  }, [isOpen, t.modals.group.defaultRulesText]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    const trimmedName = name.trim();
    if (!trimmedName) {
      next.name = t.errors.groupNameRequired;
    } else if (trimmedName.length > FORM_LIMITS.groupName.max) {
      next.name = t.errors.groupNameTooLong;
    }
    if (categorySlugs.length === 0) {
      next.categories = t.errors.groupCategoryRequired;
    }
    const trimmedDescription = description.trim();
    if (!trimmedDescription) {
      next.description = t.errors.groupAboutRequired;
    } else if (trimmedDescription.length > FORM_LIMITS.groupDescription.max) {
      next.description = t.errors.groupDescriptionTooLong;
    }
    return next;
  };

  // MWBI-867 — run shared content moderation over the human-text fields only
  // (name + description + rulesText). websiteLink is a URL and is deliberately
  // excluded. Joined with "\n" so the inappropriate-word `\b…\b` regex never
  // matches across a field boundary. Crisis is informational (never blocks);
  // submission is gated on `hasInappropriate`. Called before the `!isOpen`
  // early return to satisfy the Rules of Hooks.
  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(`${name}\n${description}\n${rulesText}`);

  if (!isOpen) return null;

  const handleFiles = async (files: FileList | File[]) => {
    const allImageFiles = Array.from(files).filter((file) => file.type.startsWith("image/"));
    const availableSlots = Math.max(0, MAX_IMAGES - images.length);
    const filesToAdd = allImageFiles.slice(0, availableSlots);
    if (filesToAdd.length === 0) return;
    const { prepared, anyCompressed, error } = await prepareImageUploads(filesToAdd, language);
    if (error) { setImageError(error); setImageNotice(null); return; }
    setImageError(null);
    setImageNotice(anyCompressed ? photosCopy.compressedNotice : null);
    setImages((prev) => [...prev, ...prepared]);
    setPreviews((prev) => [...prev, ...prepared.map((f) => URL.createObjectURL(f))]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index: number) => {
    const url = previews[index];
    if (url) URL.revokeObjectURL(url);
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) setImageNotice(null);
      return next;
    });
  };

  const handleAvatarFile = async (file: File) => {
    const { file: prepared, compressed, error } = await prepareAvatarUpload(file, language);
    if (error) { setAvatarError(error); setAvatarNotice(null); return; }
    setAvatarError(null);
    setAvatarNotice(compressed ? avatarCopy.compressedNotice : null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatar(prepared);
    setAvatarPreview(URL.createObjectURL(prepared));
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleAvatarFile(file);
  };

  const handleDropAvatar = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleAvatarFile(file);
  };

  const handleDropPhotos = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) void handleFiles(e.dataTransfer.files);
  };

  const removeAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatar(null);
    setAvatarPreview(null);
    setAvatarNotice(null);
  };

  const resetForm = () => {
    previews.forEach((url) => URL.revokeObjectURL(url));
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setName("");
    setDescription("");
    setWebsiteLink("");
    setCategorySlugs([]);
    setAcceptingMembers(true);
    setMemberApprovalMode('manual');
    setMemberListVisible(true);
    setEventListVisible(true);
    setImageError(null);
    setImageNotice(null);
    setImages([]);
    setPreviews([]);
    setAvatar(null);
    setAvatarPreview(null);
    setAvatarError(null);
    setAvatarNotice(null);
    setRulesText(t.modals.group.defaultRulesText);
    setErrors({});
  };

  const handleSubmit = async () => {
    // MWBI-867 — defense-in-depth: block submission while inappropriate
    // language is present even if the disabled button is bypassed. Crisis
    // detection is informational and never blocks here.
    if (loading || hasInappropriate) return;
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    setLoading(true);
    const result = await onCreate({
      name: name.trim(),
      description: description.trim(),
      categorySlugs,
      websiteLink: websiteLink.trim(),
      acceptingMembers,
      // MWBI-890 — each switch drives its own flag. Previously both were
      // derived from one Public/Private switch, which also set `isPrivate`
      // (MWBI-739). `isPrivate` is no longer sent from here at all.
      memberListVisible,
      eventListVisible,
      memberApprovalMode,
      image: images[0] ?? null,
      images,
      avatar,
      rulesText: rulesText.trim(),
    });
    setLoading(false);
    if (result.success) {
      resetForm();
      onClose();
    }
  };

  const pickUpToLabel = t.modals.group.pickUpToCategories.replace("{n}", String(MAX_CATEGORIES));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-group-root">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.modals.group.createGroup}</h2>
            <p className="text-sm text-foreground-muted mt-0.5">{t.modals.group.createGroupSubtitle}</p>
          </div>
          <button onClick={onClose} data-testid="create-group-close-btn" className="p-1 hover:bg-sidebar-hover rounded transition ml-4 flex-shrink-0">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-5">

          {/* MWBI-867 — content moderation: crisis advisory (informational,
              never blocks) + inappropriate-language ack modal & inline banner
              (blocks submission while present). */}
          <CrisisModal
            open={showCrisisModal}
            onClose={dismissCrisisModal}
            testIdPrefix="create-group-crisis"
          />

          <InappropriateLanguageModal
            open={showInappropriateModal}
            onClose={dismissInappropriateModal}
            testIdPrefix="create-group-inappropriate"
          />

          {hasInappropriate && (
            <InappropriateLanguageBanner testIdPrefix="create-group-inappropriate" />
          )}

          {/* Group Name */}
          <div>
            <label
              htmlFor="create-group-name-input"
              className="flex justify-between items-baseline text-sm font-medium text-foreground mb-1.5"
            >
              <span>
                {t.modals.group.groupName} <span className="text-red-500">*</span>
              </span>
              <span className="text-xs text-foreground-muted font-normal">
                {name.length}/{FORM_LIMITS.groupName.max}
              </span>
            </label>
            <input
              id="create-group-name-input"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              placeholder={t.modals.group.enterGroupName}
              maxLength={FORM_LIMITS.groupName.max}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? "create-group-name-error" : undefined}
              data-testid="create-group-name-input"
              className={`w-full px-3 py-2 border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${errors.name ? 'border-red-500' : 'border-border'}`}
            />
            {errors.name && (
              <FieldError
                id="create-group-name-error"
                data-testid="create-group-name-error"
              >
                {errors.name}
              </FieldError>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t.modals.group.category} <span className="text-red-500">*</span>
            </label>
            <select
              value=""
              onChange={(e) => {
                const value = e.target.value;
                if (value && !categorySlugs.includes(value) && categorySlugs.length < MAX_CATEGORIES) {
                  setCategorySlugs([...categorySlugs, value]);
                  if (errors.categories) setErrors({ ...errors, categories: undefined });
                }
              }}
              disabled={categorySlugs.length >= MAX_CATEGORIES}
              aria-invalid={Boolean(errors.categories)}
              aria-describedby={errors.categories ? "create-group-category-error" : undefined}
              data-testid="create-group-category-select"
              className={`w-full px-3 py-2 border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${errors.categories ? 'border-red-500' : 'border-border'}`}
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
            >
              <option value="">{t.modals.group.selectCategory}</option>
              {sortByLabel(
                availableCategories.filter((cat) => !cat.archived && !categorySlugs.includes(cat.slug)),
                (cat) => getGroupCategoryLabelI18n(t, cat.slug, cat.name),
                language,
              ).map((cat) => (
                <option key={cat.slug} value={cat.slug}>
                  {getGroupCategoryLabelI18n(t, cat.slug, cat.name)}
                </option>
              ))}
            </select>
            <p className="text-xs text-foreground-muted mt-1">{pickUpToLabel}</p>
            {errors.categories && (
              <FieldError
                id="create-group-category-error"
                data-testid="create-group-category-error"
              >
                {errors.categories}
              </FieldError>
            )}
            {categorySlugs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {categorySlugs.map((slug) => {
                  const option = availableCategories.find((c) => c.slug === slug);
                  return (
                    <span
                      key={slug}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-full"
                    >
                      {option ? getGroupCategoryLabelI18n(t, option.slug, option.name) : slug}
                      <X
                        size={12}
                        data-testid={`create-group-remove-category-chip-${slug}`}
                        className="cursor-pointer hover:text-primary-dark"
                        onClick={() => setCategorySlugs(categorySlugs.filter((c) => c !== slug))}
                      />
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Group Photos */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t.modals.group.groupPhotos} ({previews.length}/{MAX_IMAGES})
            </label>
            {previews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                {previews.map((preview, index) => (
                  <div key={preview} className="relative aspect-square rounded-md overflow-hidden border border-border bg-background-secondary">
                    <Image src={preview} alt={`Photo ${index + 1}`} fill className="object-cover" unoptimized />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      data-testid={`create-group-remove-image-btn-${index}`}
                      className="absolute top-2 right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600 transition"
                      aria-label={`Remove photo ${index + 1}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div
              onDrop={handleDropPhotos}
              onDragOver={(e) => e.preventDefault()}
            >
              <button
                type="button"
                onClick={() => { if (images.length < MAX_IMAGES) fileInputRef.current?.click(); }}
                disabled={images.length >= MAX_IMAGES}
                data-testid="create-group-images-drop-zone-btn"
                className="w-full h-32 rounded-md border border-dashed border-border bg-background-secondary flex flex-col items-center justify-center gap-1.5 hover:bg-sidebar-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImagePlus size={28} className="text-foreground-muted" />
                <span className="text-sm text-foreground-muted">{t.modals.group.addPhoto}</span>
              </button>
              <p className="text-xs text-foreground-muted mt-1.5 text-center">{photosCopy.fileTypes}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                data-testid="create-group-images-input"
                className="hidden"
                onChange={(e) => { if (e.target.files) void handleFiles(e.target.files); }}
              />
            </div>
            {imageError && <p className="text-xs text-error mt-1">{imageError}</p>}
            {!imageError && imageNotice && <p className="text-xs text-foreground-muted mt-1">{imageNotice}</p>}
          </div>

          {/* Group Avatar */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t.modals.group.groupAvatar}
            </label>
            <div
              onDrop={handleDropAvatar}
              onDragOver={(e) => e.preventDefault()}
            >
              {avatarPreview ? (
                <div className="relative w-32 h-32 rounded-md overflow-hidden border border-border bg-background-secondary mx-auto">
                  <Image src={avatarPreview} alt={t.modals.group.groupAvatar} fill className="object-cover" unoptimized />
                  <button
                    type="button"
                    onClick={removeAvatar}
                    className="absolute top-1 right-1 bg-error text-white rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  data-testid="create-group-avatar-drop-zone-btn"
                  className="w-full h-32 rounded-md border border-dashed border-border bg-background-secondary flex flex-col items-center justify-center gap-1.5 hover:bg-sidebar-hover transition"
                >
                  <ImagePlus size={28} className="text-foreground-muted" />
                  <span className="text-sm text-foreground-muted">{t.modals.group.addAvatar}</span>
                </button>
              )}
            </div>
            {avatarPreview && (
              <div className="flex justify-center mt-2">
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="text-xs text-primary hover:underline"
                >
                  {t.modals.group.addAvatar}
                </button>
              </div>
            )}
            <p className="text-xs text-foreground-muted mt-1.5 text-center">{avatarCopy.fileTypes}</p>
            {avatarError && <p className="text-xs text-error mt-1 text-center">{avatarError}</p>}
            {!avatarError && avatarNotice && <p className="text-xs text-foreground-muted mt-1 text-center">{avatarNotice}</p>}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleAvatarChange}
              data-testid="create-group-avatar-input"
              className="hidden"
            />
          </div>

          {/* About Us */}
          <div>
            <label
              htmlFor="create-group-description-textarea"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {t.modals.group.aboutUs} <span className="text-red-500">*</span>
            </label>
            <textarea
              id="create-group-description-textarea"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors({ ...errors, description: undefined });
              }}
              placeholder={t.modals.group.tellUsMore}
              rows={4}
              maxLength={FORM_LIMITS.groupDescription.max}
              aria-invalid={Boolean(errors.description)}
              aria-describedby={errors.description ? "create-group-description-error" : undefined}
              data-testid="create-group-description-textarea"
              className={`w-full px-3 py-2 border rounded-md text-sm text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${errors.description ? 'border-red-500' : 'border-border'}`}
            />
            {errors.description && (
              <FieldError
                id="create-group-description-error"
                data-testid="create-group-description-error"
              >
                {errors.description}
              </FieldError>
            )}
          </div>

          {/* Group Rules */}
          <div>
            <label
              htmlFor="create-group-rules-textarea"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {t.modals.group.groupRulesLabel}
            </label>
            <textarea
              id="create-group-rules-textarea"
              data-testid="create-group-rules-textarea"
              value={rulesText}
              onChange={(e) => setRulesText(e.target.value)}
              rows={7}
              maxLength={4000}
              className="w-full px-3 py-2 border border-border rounded-md text-sm text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-foreground-muted mt-1" data-testid="create-group-rules-hint">{t.modals.group.rulesHint}</p>
            <p className="mt-1 text-xs text-foreground-muted text-right">{rulesText.length}/4000</p>
          </div>

          {/* Website Link */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t.modals.group.websiteLink}
            </label>
            <input
              type="url"
              value={websiteLink}
              onChange={(e) => setWebsiteLink(e.target.value)}
              placeholder="https://..."
              data-testid="create-group-website-input"
              className="w-full px-3 py-2 border border-border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* MWBI-890 — the Create popup keeps the app's segmented pill style
              (its Figma panel, and the control the Accepting Members / Approval
              Mode settings already used). The green sliding switch is the Edit
              Group form's control, per that panel. Each control is independent:
              no control derives its value from another. */}
          <div className="space-y-4">
            <SegmentedToggle
              leftLabel={t.modals.group.acceptingMembers}
              rightLabel={t.modals.group.groupIsFull}
              leftSelected={acceptingMembers}
              onSelectLeft={() => setAcceptingMembers(true)}
              onSelectRight={() => setAcceptingMembers(false)}
              testIdPrefix="create-group-accepting-members"
            />

            <div>
              <SegmentedToggle
                leftLabel={t.modals.group.manualReview}
                rightLabel={t.modals.group.autoAccept}
                leftSelected={memberApprovalMode === 'manual'}
                onSelectLeft={() => setMemberApprovalMode('manual')}
                onSelectRight={() => setMemberApprovalMode('auto')}
                testIdPrefix="create-group-approval-mode"
              />
              <p className="text-xs text-foreground-muted mt-2 leading-relaxed">
                {t.modals.group.memberApprovalNote}
              </p>
            </div>

            <SegmentedToggle
              leftLabel={t.modals.group.showMemberList}
              rightLabel={t.modals.group.hideMemberList}
              leftSelected={memberListVisible}
              onSelectLeft={() => setMemberListVisible(true)}
              onSelectRight={() => setMemberListVisible(false)}
              testIdPrefix="create-group-member-list"
            />

            <div>
              <SegmentedToggle
                leftLabel={t.modals.group.showGroupEvents}
                rightLabel={t.modals.group.hideGroupEvents}
                leftSelected={eventListVisible}
                onSelectLeft={() => setEventListVisible(true)}
                onSelectRight={() => setEventListVisible(false)}
                testIdPrefix="create-group-group-events"
              />
              <p className="text-xs text-foreground-muted mt-2 leading-relaxed">
                {t.modals.group.groupVisibilityNote}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button
            onClick={onClose}
            disabled={loading}
            data-testid="create-group-cancel-btn"
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
          >
            {t.common.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || hasInappropriate}
            data-testid="create-group-submit-btn"
            className="px-5 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t.modals.group.creating : t.modals.group.createGroupBtn}
          </button>
        </div>
      </div>
    </div>
  );
}
